import { Box, Typography } from '@mui/material'
import { PerformanceTab, QuestionnairePerformance, ChartDataPoint, ChartSeries, PieChartStoreData, QueryMode } from '../types'
import { ResultItemList } from './ResultItemList'
import { PerformanceChart } from './PerformanceChart'
import { Pagination } from './Pagination'
import { ProcessStatusPanel } from './ProcessStatusPanel'
import { ProcessStatusQueryParams } from '../types'
import { useStorePerformanceContext } from '../context/StorePerformanceContext'
import { getScoreColor } from '../../../../../../../utils/methods/chartColors'

interface PerformanceResultsProps {
  activeTab: PerformanceTab
  onTabChange: (tab: PerformanceTab) => void
  selectedResultIds: string[]
  toggleResultItem: (id: string) => void
  selectAllResultItems: () => void
  searchResult: string
  setSearchResult: (value: string) => void
  paginatedResultItems: QuestionnairePerformance[]
  filteredResultItems: QuestionnairePerformance[]
  resultsPage: number
  setResultsPage: (page: number) => void
  totalPages: number
  resultListTitle: string
  chartType: 'bar' | 'line' | 'pie'
  onChartTypeChange: (type: 'bar' | 'line' | 'pie') => void
  chartData: ChartDataPoint[]
  chartSeries: ChartSeries[]
  chartColors: (index: number) => string
  globalAverageScore: number | null
  pieChartData: PieChartStoreData[]
  pieChartLoading: boolean
  pieChartPage: number
  pieChartTotalPages: number
  onPieChartPageChange: (page: number) => void
  queryMode: QueryMode
  processStatusQueryParams: ProcessStatusQueryParams
  scoreMap: Map<string, number>
  hasQueried: boolean
  onRangeApplied?: (range: [number, number]) => void
}

const PerformanceResults = ({
  activeTab,
  onTabChange,
  selectedResultIds,
  toggleResultItem,
  selectAllResultItems,
  searchResult,
  setSearchResult,
  paginatedResultItems,
  filteredResultItems,
  resultsPage,
  setResultsPage,
  totalPages,
  resultListTitle,
  chartType,
  onChartTypeChange,
  chartData,
  chartSeries,
  chartColors,
  globalAverageScore,
  pieChartData,
  pieChartLoading,
  pieChartPage,
  pieChartTotalPages,
  onPieChartPageChange,
  queryMode,
  processStatusQueryParams,
  scoreMap,
  hasQueried,
  onRangeApplied,
}: PerformanceResultsProps) => {
  const { styles, colors, icons, t } = useStorePerformanceContext()

  return (
    <Box sx={styles.section}>
      {/* Tabs */}
      <Box sx={styles.tabsContainer}>
        <Box
          sx={activeTab === 'performance' ? styles.tabButtonActive : styles.tabButton}
          onClick={() => onTabChange('performance')}
          role="tab"
          aria-selected={activeTab === 'performance'}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onTabChange('performance')
            }
          }}
        >
          {t('dashboard.storePerformance.tabs.performance')}
        </Box>
        <Box
          sx={activeTab === 'process-status' ? styles.tabButtonActive : styles.tabButton}
          onClick={() => onTabChange('process-status')}
          role="tab"
          aria-selected={activeTab === 'process-status'}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onTabChange('process-status')
            }
          }}
        >
          {t('dashboard.storePerformance.tabs.processStatus')}
        </Box>
      </Box>

      {/* Content */}
      {activeTab === 'performance' ? (
        <Box sx={{
          ...styles.resultsLayout,
          ...(chartType === 'pie' ? { 
            display: 'flex',
            minHeight: '380px',
            position: 'relative',
            gap: 0,
            flexWrap: 'nowrap',
          } : {}),
        }}>
          <ResultItemList
            data={paginatedResultItems}
            selectedIds={selectedResultIds}
            searchValue={searchResult}
            onSearchChange={setSearchResult}
            onToggle={toggleResultItem}
            onSelectAll={selectAllResultItems}
            page={resultsPage}
            totalPages={totalPages}
            onPageChange={setResultsPage}
            listTitle={resultListTitle}
            styles={{
              questionnaireListPanel: {
                ...styles.questionnaireListPanel,
                ...(chartType === 'pie' ? { 
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: '280px',
                  maxHeight: 'none',
                  height: 'auto',
                  zIndex: 1,
                } : {}),
              },
              questionnaireListHeader: styles.questionnaireListHeader,
              questionnaireListSearch: styles.questionnaireListSearch,
              questionnaireItem: styles.questionnaireItem,
              questionnaireItemActive: styles.questionnaireItemActive,
              questionnaireItemSelected: styles.questionnaireItemSelected,
              questionnaireItemText: styles.questionnaireItemText,
              badgeRed: styles.badgeRed,
              badgeYellow: styles.badgeYellow,
              badgeGreen: styles.badgeGreen,
              paginationContainer: styles.paginationContainer,
              paginationButton: styles.paginationButton,
              paginationPage: styles.paginationPage,
              paginationPageActive: styles.paginationPageActive,
              paginationDisabled: styles.paginationDisabled,
            }}
            colors={colors}
            icons={icons}
            t={t}
            queryMode={queryMode}
          />
          <Box sx={{
            ...styles.chartPanel,
            ...(chartType === 'pie' ? {
              height: 'auto',
              maxHeight: 'none',
              marginLeft: '296px',
              flex: 1,
            } : {}),
          }}>
            <Box sx={styles.chartPanelHeader}>
              <Box>
                <Typography sx={styles.chartPanelTitle}>
                  {t('dashboard.storePerformance.tabs.performance')}
                </Typography>
                {queryMode === 'one-unit-many-questionnaires' && globalAverageScore !== null && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Typography sx={{ fontSize: '13px', color: colors.color9 }}>
                      {t('dashboard.storePerformance.chart.globalAverage')}
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: getScoreColor(globalAverageScore, colors),
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        minWidth: '36px',
                        textAlign: 'center',
                      }}
                    >
                      {globalAverageScore}%
                    </Box>
                  </Box>
                )}
              </Box>
              <Box sx={styles.chartPanelIcons}>
                <Box
                  sx={{
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    backgroundColor: chartType === 'bar' ? 'rgba(21,139,200,0.09)' : '#f3f4f6',
                    color: chartType === 'bar' ? '#158bc8' : '#9ca3af',
                  }}
                  onClick={() => onChartTypeChange('bar')}
                >
                  {icons('BarChart')}
                </Box>
                <Box
                  sx={{
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    backgroundColor: chartType === 'line' ? 'rgba(21,139,200,0.09)' : '#f3f4f6',
                    color: chartType === 'line' ? '#158bc8' : '#9ca3af',
                  }}
                  onClick={() => onChartTypeChange('line')}
                >
                  {icons('ShowChart')}
                </Box>
                <Box
                  sx={{
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    backgroundColor: chartType === 'pie' ? 'rgba(21,139,200,0.09)' : '#f3f4f6',
                    color: chartType === 'pie' ? '#158bc8' : '#9ca3af',
                  }}
                  onClick={() => onChartTypeChange('pie')}
                >
                  {icons('PieChart')}
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <PerformanceChart
                data={chartData}
                series={chartSeries}
                colors={chartColors}
                chartType={chartType}
                pieChartData={pieChartData}
                pieChartLoading={pieChartLoading}
                queryMode={queryMode}
                t={t}
                totalResultCount={filteredResultItems.length}
                selectedResultIds={selectedResultIds}
              />
              {chartType === 'pie' && pieChartTotalPages > 1 && (
                <Box sx={{ ...styles.paginationContainer, justifyContent: 'flex-end', padding: '0 20px 18px 0' }}>
                  <Pagination
                    page={pieChartPage}
                    totalPages={pieChartTotalPages}
                    onPageChange={onPieChartPageChange}
                    maxVisiblePages={5}
                    styles={{
                      paginationContainer: styles.paginationContainer,
                      paginationButton: styles.paginationButton,
                      paginationPage: styles.paginationPage,
                      paginationPageActive: styles.paginationPageActive,
                      paginationDisabled: styles.paginationDisabled,
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <ProcessStatusPanel
          queryParams={processStatusQueryParams}
          queryMode={queryMode}
          scoreMap={scoreMap}
          enabled={hasQueried && activeTab === 'process-status'}
          onRangeApplied={onRangeApplied}
          styles={{
            processStatusLayout: styles.processStatusLayout,
            processPanel: styles.processPanel,
            processPanelHeader: styles.processPanelHeader,
            processPanelTitle: styles.processPanelTitle,
            processPanelHeaderRight: styles.processPanelHeaderRight,
            sliderContainer: styles.sliderContainer,
            applyButton: styles.applyButton,
            searchField: styles.searchField,
            questionnaireHeader: styles.questionnaireHeader,
            questionnaireHeaderText: styles.questionnaireHeaderText,
            questionnaireHeaderArrow: styles.questionnaireHeaderArrow,
            sectionBarBlue: styles.sectionBarBlue,
            sectionBarBlueText: styles.sectionBarBlueText,
            questionBarGrayContainer: styles.questionBarGrayContainer,
            questionBarGrayLabel: styles.questionBarGrayLabel,
            questionBarGrayTrack: styles.questionBarGrayTrack,
            questionBarGrayFill: styles.questionBarGrayFill,
            questionBarGrayValue: styles.questionBarGrayValue,
            globalProcessRow: styles.globalProcessRow,
            globalProcessIndicator: styles.globalProcessIndicator,
            globalProcessName: styles.globalProcessName,
            globalProcessTrack: styles.globalProcessTrack,
            globalProcessFill: styles.globalProcessFill,
            globalProcessScore: styles.globalProcessScore,
            rightQuestionnaireRow: styles.rightQuestionnaireRow,
            rightQuestionnaireName: styles.rightQuestionnaireName,
            rightQuestionnaireBadge: styles.rightQuestionnaireBadge,
            rightQuestionnaireArrow: styles.rightQuestionnaireArrow,
            rightSectionRow: styles.rightSectionRow,
            rightSectionLeftColumn: styles.rightSectionLeftColumn,
            rightSectionRightColumn: styles.rightSectionRightColumn,
            rightSectionIndicator: styles.rightSectionIndicator,
            rightSectionName: styles.rightSectionName,
            rightSectionTrack: styles.rightSectionTrack,
            rightSectionFill: styles.rightSectionFill,
            rightSectionScore: styles.rightSectionScore,
            toggleButtonGroup: styles.toggleButtonGroup,
            toggleButton: styles.toggleButton,
            toggleButtonActive: styles.toggleButtonActive,
            processDivider: styles.processDivider,
            paginationContainer: styles.paginationContainer,
            paginationButton: styles.paginationButton,
            paginationPage: styles.paginationPage,
            paginationPageActive: styles.paginationPageActive,
            paginationDisabled: styles.paginationDisabled,
          }}
          icons={icons}
          t={t}
        />
      )}
    </Box>
  )
}

export { PerformanceResults }
